import React, { useCallback, useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { addexercise } from "../funtionsBD/Mainservice";
import {
  buildCoachHistorySessionPlan,
  visiblePicksForBlock,
} from "../../utils/coachSessionExerciseHistory";
import "./CoachHistorySessionPlanDialog.css";

/**
 * Live Training Plan AI — last-month coach history:
 * common types → blocks; 3 done + 3 never-done; Show more → +3/+3.
 */
export function CoachHistorySessionPlanDialog({
  open,
  onClose,
  tsId,
  grId,
  tsgId = null,
  targetDuration = 90,
  language = "en",
  container,
  onApplied,
}) {
  const { t } = useTranslation();
  const lng = String(language || "en").slice(0, 2);

  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");
  const [plan, setPlan] = useState(null);
  const [expandedByType, setExpandedByType] = useState({});
  const [selections, setSelections] = useState({});

  const reset = useCallback(() => {
    setPlan(null);
    setExpandedByType({});
    setSelections({});
    setError("");
    setLoading(false);
    setApplying(false);
  }, []);

  const handleClose = useCallback(() => {
    if (applying) return;
    reset();
    onClose?.();
  }, [applying, onClose, reset]);

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    if (!tsId || !grId) {
      setError(
        t("selectSessionAndGroup") || "Please select a session and group"
      );
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      setPlan(null);
      setExpandedByType({});
      setSelections({});
      try {
        const data = await buildCoachHistorySessionPlan({
          grId,
          excludeTsId: tsId,
          language: lng,
          targetDuration,
        });
        if (cancelled) return;
        setPlan(data);
        const initial = {};
        for (const block of data.blocks || []) {
          const { picks } = visiblePicksForBlock(block, false);
          if (picks[0]?.ex_id) initial[block.type_id] = picks[0].ex_id;
        }
        setSelections(initial);
        if (!data.blocks?.length) {
          setError(
            t(
              "liveTrainingHistoryPlanEmpty",
              "No exercises found in the last month for this group. Add a few sessions first, or pick a template."
            )
          );
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.message ||
              t("liveTrainingHistoryPlanError") ||
              "Could not build a plan from last month’s sessions."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, tsId, grId, lng, targetDuration, reset, t]);

  const totalSelected = useMemo(
    () => Object.values(selections).filter(Boolean).length,
    [selections]
  );

  const handleApply = async () => {
    if (!tsId || !plan?.blocks?.length || applying) return;
    const exIds = plan.blocks
      .map((block) => {
        const exId = selections[block.type_id];
        if (!exId) return null;
        const fromDone = block.done.find((e) => String(e.ex_id) === String(exId));
        const fromNever = block.never.find(
          (e) => String(e.ex_id) === String(exId)
        );
        const pick = fromDone || fromNever;
        return {
          ex_id: exId,
          duration:
            parseInt(block.duration_minutes, 10) ||
            parseInt(pick?.duration, 10) ||
            10,
          steps: 1,
        };
      })
      .filter(Boolean);
    if (!exIds.length) return;
    try {
      setApplying(true);
      setError("");
      await addexercise({
        ts_id: tsId,
        ex_ids: exIds,
        allow_duration_exceed: true,
        tsg_id: tsgId || undefined,
      });
      reset();
      onClose?.();
      onApplied?.();
    } catch (err) {
      setError(
        err?.message ||
          t("errorApplyingTemplate") ||
          "Error applying session plan"
      );
    } finally {
      setApplying(false);
    }
  };

  const busy = loading || applying;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      className="lt-history-plan-dialog"
      container={container || undefined}
      disableScrollLock={Boolean(container)}
      sx={{ zIndex: 5000 }}
      PaperProps={{ className: "lt-history-plan-dialog__paper" }}
    >
      <header className="lt-history-plan-dialog__header">
        <div>
          <p className="lt-history-plan-dialog__eyebrow">
            <AutoAwesomeIcon fontSize="inherit" aria-hidden />
            {t("liveTrainingHistoryPlanEyebrow", "From your last month")}
          </p>
          <h2 className="lt-history-plan-dialog__title">
            {t("liveTrainingHistoryPlanTitle", "Plan from coach history")}
          </h2>
          {plan ? (
            <p className="lt-history-plan-dialog__meta">
              {t("liveTrainingHistoryPlanMeta", {
                count: plan.session_count,
                from: plan.date_from,
                to: plan.date_to,
                defaultValue:
                  "{{count}} sessions · {{from}} → {{to}}. One pick per common type.",
              })}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className="lt-history-plan-dialog__close"
          onClick={handleClose}
          disabled={applying}
          aria-label={t("close") || "Close"}
        >
          <CloseIcon fontSize="small" />
        </button>
      </header>

      <div className="lt-history-plan-dialog__body">
        {loading ? (
          <p className="lt-history-plan-dialog__status">
            {t("loading") || "Loading…"}
          </p>
        ) : null}

        {error ? (
          <p className="lt-history-plan-dialog__error" role="alert">
            {error}
          </p>
        ) : null}

        {!loading && plan?.blocks?.length
          ? plan.blocks.map((block) => {
              const expanded = Boolean(expandedByType[block.type_id]);
              const { picks, canExpand } = visiblePicksForBlock(block, expanded);
              const selectedId = selections[block.type_id];
              return (
                <section
                  key={block.type_id}
                  className="lt-history-plan-dialog__block"
                >
                  <header className="lt-history-plan-dialog__block-head">
                    <div>
                      <h3>{block.type_name}</h3>
                      <p>
                        {t("liveTrainingHistoryPlanBlockMeta", {
                          times: block.times_used,
                          minutes: block.duration_minutes,
                          defaultValue:
                            "Used {{times}}× · ~{{minutes}} min block",
                        })}
                      </p>
                    </div>
                  </header>
                  <ul className="lt-history-plan-dialog__list">
                    {picks.map((pick) => {
                      const active = String(selectedId) === String(pick.ex_id);
                      return (
                        <li key={`${block.type_id}-${pick.ex_id}-${pick.bucket}`}>
                          <button
                            type="button"
                            className={`lt-history-plan-dialog__pick${
                              active ? " is-active" : ""
                            }`}
                            onClick={() =>
                              setSelections((prev) => ({
                                ...prev,
                                [block.type_id]: pick.ex_id,
                              }))
                            }
                          >
                            <span className="lt-history-plan-dialog__pick-name">
                              {pick.name}
                            </span>
                            <span
                              className={`lt-history-plan-dialog__tag lt-history-plan-dialog__tag--${pick.bucket}`}
                            >
                              {pick.bucket === "done"
                                ? t("liveTrainingHistoryPlanDone", {
                                    count: pick.count,
                                    defaultValue: "Done {{count}}×",
                                  })
                                : t(
                                    "liveTrainingHistoryPlanNew",
                                    "Not done yet"
                                  )}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  {canExpand ? (
                    <button
                      type="button"
                      className="lt-history-plan-dialog__more"
                      onClick={() =>
                        setExpandedByType((prev) => ({
                          ...prev,
                          [block.type_id]: true,
                        }))
                      }
                    >
                      <ExpandMoreIcon fontSize="small" />
                      {t(
                        "liveTrainingHistoryPlanShowMore",
                        "Show more · 3 done + 3 new"
                      )}
                    </button>
                  ) : null}
                </section>
              );
            })
          : null}
      </div>

      <footer className="lt-history-plan-dialog__footer">
        <button
          type="button"
          className="lt-history-plan-dialog__cancel"
          onClick={handleClose}
          disabled={applying}
        >
          {t("close") || "Close"}
        </button>
        <button
          type="button"
          className="lt-history-plan-dialog__apply"
          onClick={handleApply}
          disabled={busy || totalSelected === 0}
        >
          {applying
            ? t("applying") || "Applying…"
            : t("liveTrainingHistoryPlanApply", {
                count: totalSelected,
                defaultValue: "Add {{count}} blocks to session",
              })}
        </button>
      </footer>
    </Dialog>
  );
}

export default CoachHistorySessionPlanDialog;
